import { ApiError } from '../features/identity/types';

/**
 * EPN-105 — the browser side of the server's subscription-state gate.
 *
 * When the server refuses a write because of the company's subscription state it answers `403` with one of three
 * stable codes (see `RequireSubscriptionAccessFilter` and `RequireFeatureEntitlementFilter`):
 *  - `SUBSCRIPTION_ACCESS_RESTRICTED` — the company's access mode (read-only / setup-only) does not allow this write;
 *  - `SUBSCRIPTION_REQUIRED` — the subscription is not currently granting plan features (trial not started, trial
 *    ended, canceled);
 *  - `SUBSCRIPTION_UPGRADE_REQUIRED` — the plan is too low for the feature.
 *
 * Everything here only TRANSLATES what the server said into plain language and a next step. It never decides
 * whether something is allowed — the server already did. It is deliberately framework-free so the shared HTTP
 * client can use it without importing React.
 */
export const SUBSCRIPTION_RESTRICTION_CODES = [
  'SUBSCRIPTION_ACCESS_RESTRICTED',
  'SUBSCRIPTION_REQUIRED',
  'SUBSCRIPTION_UPGRADE_REQUIRED',
] as const;

export type SubscriptionRestrictionCode = (typeof SUBSCRIPTION_RESTRICTION_CODES)[number];

export interface SubscriptionRestriction {
  code: SubscriptionRestrictionCode;
  /** The server's effective state (`TrialExpired`, `PastDueRestricted`, ...), when it sent one. */
  subscriptionState: string | null;
  accessMode: string | null;
  /** `StartTrial`, `ChoosePlan`, `UpdatePayment`, `Reactivate`, or null. */
  requiredAction: string | null;
  reason: string | null;
  currentPlan: string | null;
  requiredPlan: string | null;
  /** `SUBSCRIPTION_UPGRADE_REQUIRED` only: the catalog key and display name of the feature the plan is too low for. */
  featureKey?: string | null;
  featureName?: string | null;
  /** `SUBSCRIPTION_UPGRADE_REQUIRED` only: the server's plain-words "what changes if you upgrade" (the feature's upgrade message). */
  message?: string | null;
}

export interface RestrictionAction {
  label: string;
  to: string;
}

export function isSubscriptionRestrictionCode(code: unknown): code is SubscriptionRestrictionCode {
  return typeof code === 'string' && (SUBSCRIPTION_RESTRICTION_CODES as readonly string[]).includes(code);
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** Returns the restriction described by a failed response, or null when the response is anything else (an ordinary
 * permission 403, a validation error, ...). Only a 403 carrying one of the three codes counts. */
export function restrictionFromProblem(status: number, body: unknown): SubscriptionRestriction | null {
  if (status !== 403 || typeof body !== 'object' || body === null) return null;
  const record = body as Record<string, unknown>;
  if (!isSubscriptionRestrictionCode(record.code)) return null;
  return {
    code: record.code,
    subscriptionState: str(record.subscriptionState),
    accessMode: str(record.accessMode),
    requiredAction: str(record.requiredAction),
    reason: str(record.reason),
    currentPlan: str(record.currentPlan),
    requiredPlan: str(record.requiredPlan),
    featureKey: str(record.featureKey),
    featureName: str(record.featureName),
    message: str(record.message),
  };
}

/** The restriction a failed call carried, or null when the error is anything else (a plain permission 403, a validation error, a network
 * fault). The shared HTTP client keeps the parsed problem body on `ApiError.details`; this reads it the same way `restrictionFromProblem` does. */
export function restrictionFromError(error: unknown): SubscriptionRestriction | null {
  if (!(error instanceof ApiError)) return null;
  return restrictionFromProblem(error.status, error.details);
}

/** The refusal for a plan that is TOO LOW for the feature (`SUBSCRIPTION_UPGRADE_REQUIRED`), or null. A subscription that is not in force
 * (`SUBSCRIPTION_REQUIRED`, trial ended, canceled, payment failed) is deliberately NOT this: an upgrade would not help, so it must never be
 * shown as an upgrade offer. */
export function upgradeRequiredFromError(error: unknown): SubscriptionRestriction | null {
  const restriction = restrictionFromError(error);
  return restriction?.code === 'SUBSCRIPTION_UPGRADE_REQUIRED' ? restriction : null;
}

/** The refusal for a subscription that is not currently granting plan features (`SUBSCRIPTION_REQUIRED`), or null. */
export function subscriptionRequiredFromError(error: unknown): SubscriptionRestriction | null {
  const restriction = restrictionFromError(error);
  return restriction?.code === 'SUBSCRIPTION_REQUIRED' ? restriction : null;
}

/** Plain, non-technical explanation of what the server refused and why. Never blames the user's role. */
export function restrictionMessage(restriction: SubscriptionRestriction): string {
  if (restriction.code === 'SUBSCRIPTION_UPGRADE_REQUIRED') {
    // The server's own words for what upgrading changes win over the generic sentence.
    if (restriction.message) return restriction.message;
    const needed = restriction.requiredPlan ? `the ${restriction.requiredPlan} plan` : 'a higher plan';
    return `This feature is included in ${needed}. Compare plans to see what each one includes.`;
  }

  const state = restriction.subscriptionState;
  switch (restriction.requiredAction) {
    case 'UpdatePayment':
      return 'Your last payment did not go through, so changes are paused. Update your payment method to continue. Your records are safe.';
    case 'Reactivate':
      return 'This subscription has been canceled, so the company is read-only. Reactivate it to make changes. Your records are safe.';
    case 'StartTrial':
      return 'Start your free trial to make this change. No credit card is needed.';
    case 'ChoosePlan':
      return state === 'TrialExpired'
        ? 'Your free trial has ended, so the company is read-only. Subscribe to make changes. Your records are safe.'
        : 'Choose a plan to make this change.';
    default:
      break;
  }

  switch (restriction.reason ?? state) {
    case 'TrialNotStarted':
      return 'Start your free trial to make this change. No credit card is needed.';
    case 'TrialExpired':
      return 'Your free trial has ended, so the company is read-only. Subscribe to make changes. Your records are safe.';
    case 'SubscriptionCanceled':
    case 'Canceled':
      return 'This subscription has been canceled, so the company is read-only. Reactivate it to make changes. Your records are safe.';
    case 'PastDueRestricted':
      return 'Your last payment did not go through, so changes are paused. Update your payment method to continue. Your records are safe.';
    default:
      return 'Your subscription does not allow this change right now. Review your billing to continue.';
  }
}

/** The one next step that resolves the restriction, chosen from the server's `requiredAction` (never guessed). */
export function restrictionAction(restriction: SubscriptionRestriction): RestrictionAction {
  if (restriction.code === 'SUBSCRIPTION_UPGRADE_REQUIRED') {
    return { label: 'Compare plans', to: '/settings/subscription' };
  }
  switch (restriction.requiredAction) {
    case 'UpdatePayment':
      return { label: 'Update payment method', to: '/settings/billing' };
    case 'Reactivate':
      return { label: 'Reactivate subscription', to: '/settings/billing' };
    case 'StartTrial':
      return { label: 'Start free trial', to: '/settings/subscription' };
    case 'ChoosePlan':
      return { label: 'Choose a plan', to: '/settings/subscription' };
    default:
      return { label: 'Review billing', to: '/settings/billing' };
  }
}

type Listener = (restriction: SubscriptionRestriction) => void;
const listeners = new Set<Listener>();

/** Called by the shared HTTP client the moment a restriction response arrives, so one global notice can react no
 * matter which screen or gateway made the call. */
export function emitSubscriptionRestriction(restriction: SubscriptionRestriction): void {
  listeners.forEach((listener) => listener(restriction));
}

export function subscribeToSubscriptionRestrictions(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const clearListeners = new Set<() => void>();

/** A screen that shows a refusal itself (the locked-feature dialog beside the form the person is filling in) calls this so the global notice
 * does not repeat the same refusal above the page. Screens that do nothing keep the global notice. */
export function clearSubscriptionRestrictionNotice(): void {
  clearListeners.forEach((listener) => listener());
}

export function subscribeToRestrictionNoticeCleared(listener: () => void): () => void {
  clearListeners.add(listener);
  return () => {
    clearListeners.delete(listener);
  };
}
